import { useCallback, useEffect, useMemo, useState } from 'react';
import { BECarouselProps } from './BECarouselProps';
import Item from './Item';
import Slider from './Slider';
import Track from './Track';

const BECarousel = ({
  activeIndex = 0,
  children,
  gap,
  isSelectable = false,
  itemWidth,
  itemHeight,
  multiplier,
  constraint,
  showDots = true,
  direction = 'horizontal',
  onInitSliderWidth,
  onInitSliderHeight,
  onChange,
  onClickItem,
}: BECarouselProps) => {
  const [trackIsActive, setTrackIsActive] = useState(false);
  const [sliderWidth, setSliderWidth] = useState(0);
  const [sliderHeight, setSliderHeight] = useState(0);
  const [activeItem, setActiveItem] = useState(activeIndex);
  const [isDragging, setIsDragging] = useState(false);

  const initSliderWidth = useCallback(
    (width: number) => {
      onInitSliderWidth?.(width);
      setSliderWidth(width);
    },
    [onInitSliderWidth]
  );

  const initSliderHeight = useCallback(
    (height: number) => {
      onInitSliderHeight?.(height);
      setSliderHeight(height);
    },
    [onInitSliderHeight]
  );

  const positions = useMemo(
    () =>
      children.map(
        (_, index) =>
          -Math.abs(
            ((direction === 'horizontal' ? itemWidth : itemHeight) + gap) *
              index
          )
      ),
    [direction, children, itemWidth, itemHeight, gap]
  );

  useEffect(() => {
    onChange?.(activeItem);
  }, [activeItem, onChange]);

  useEffect(() => {
    setActiveItem(activeIndex);
  }, [activeIndex, setActiveItem]);

  const sliderProps = {
    setTrackIsActive,
    initSliderWidth,
    initSliderHeight,
    setActiveItem,
    activeItem,
    constraint,
    direction,
    positions,
    showDots,
    gap,
  };

  const trackProps = {
    direction,
    setIsDragging,
    setTrackIsActive,
    trackIsActive,
    setActiveItem,
    sliderWidth,
    sliderHeight,
    activeItem,
    constraint,
    multiplier,
    itemWidth,
    itemHeight,
    positions,
    gap,
  };

  const itemProps = {
    setTrackIsActive,
    trackIsActive,
    setActiveItem,
    activeItem,
    constraint,
    direction,
    isDragging,
    isSelectable,
    itemWidth,
    itemHeight,
    positions,
    gap,
    onClickItem,
  };

  return (
    <Slider {...sliderProps}>
      <Track {...trackProps}>
        {children.map((child, index) => (
          <Item {...itemProps} index={index} key={index}>
            {child}
          </Item>
        ))}
      </Track>
    </Slider>
  );
};

export default BECarousel;
